// RULE 9 — the address is the plan index: the grammar a leaf's mailbox address must obey,
// and the address/member pairing a lead publishes before dispatch.
// Split out of `../hive-event.ts`, which is now the barrel over this directory.

import { z } from 'zod';
import { HIVE_TEXT_MAX_CHARS, line } from './bounds';

/**
 * A LEAF'S MAILBOX ADDRESS: a role word followed by one or more PLAN INDICES —
 * `editor-0`, `scout-2-1`, `probe-1-3`. Never a model-authored id
 * (`docs/idea/hive.md` rule 9, consequence 2).
 *
 * The rule is not stylistic. A leaf's own id is `editor:<tag>` built from model-authored
 * manifest text: it carries a `:`, may carry a `/`, and is therefore not a legal NATS
 * subject token at all (`fleet.rpc.msg.<runnerId>.<runId>.<address>`). Sanitizing it would
 * be worse than refusing it — `a/b` and `a_b` map onto the SAME address, so a correction
 * meant for one editor is delivered to the other, over a checkout both are writing.
 *
 * The grammar admits digits only after the role word, which is what makes it a PLAN INDEX
 * and not a name: `editor-0` and `scout-2-1` pass, `editor:runner-guards` and
 * `editor-runner-guards` do not. It is also a legal NATS subject token by construction —
 * no `.`, no `*`, no `>`, no whitespace.
 */
export const HiveSteerAddressSchema = z
  .string()
  .regex(
    /^[a-z][a-z0-9]*(?:-\d+)+$/,
    'a steer address is a role word plus plan indices (editor-0, scout-2-1), never a model-authored id',
  )
  .max(64);

/**
 * One reachable leaf: the address a lead may correct it on, paired with the member id that
 * address stands for (`hive.md` rule 9). Published on the LAST event before dispatch, so
 * the pairing is in hand while the correction is still worth anything.
 *
 * `member`, not `editor`: the same shape carries scout and probe addresses on the other
 * lanes, and one concept spelled two ways is how a reader learns to check both.
 */
export const HiveSteerEntrySchema = z.object({
  address: HiveSteerAddressSchema.describe(
    'hive.md rule 9: the PLAN INDEX this leaf answers on, never its model-authored id',
  ),
  member: line(HIVE_TEXT_MAX_CHARS).describe(
    'hive.md rule 9: the member id the address stands for — model-authored, never a subject token',
  ),
});
export type HiveSteerEntry = z.infer<typeof HiveSteerEntrySchema>;
